import { initComponent } from './core/init-component';

// One observer for every card: warms the hover image into the HTTP cache before the pointer reaches it.
const hoverPreloadMql = window.matchMedia('(min-width: 1024px)');
let hoverPreloader: IntersectionObserver | null = null;

function observeHoverPreload(card: HTMLElement) {
  if (!card.dataset.hoverImage || !('IntersectionObserver' in window)) return;
  hoverPreloader ??= new IntersectionObserver((entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting || !hoverPreloadMql.matches) return;
      const target = entry.target as HTMLElement;
      const preload = new Image();
      preload.setAttribute('fetchpriority', 'low');
      preload.src = target.dataset.hoverImage ?? '';
      observer.unobserve(target);
    });
  }, { rootMargin: '300px 0px' });
  hoverPreloader.observe(card);
}

initComponent('.js-ls-product-card', (el) => {
  const img = el.querySelector<HTMLImageElement>('.js-ls-product-card__img');
  const hoverImage = el.dataset.hoverImage;
  observeHoverPreload(el);
  const isVideoOverlay = img?.hasAttribute('data-video-overlay') ?? false;
  const originalSrc = isVideoOverlay ? '' : (img?.src ?? '');

  // Selector units are <a href> when in stock (native open-in-new-tab) and
  // <button> when OOS (they open the BIS drawer). Both are matched here by
  // attribute, so the type is the shared HTMLElement.
  const colourButtons = el.querySelectorAll<HTMLElement>(
    '.js-ls-product-card__overlay [data-colour]'
  );
  const sizeGroups = el.querySelectorAll<HTMLElement>(
    '.js-ls-product-card__overlay [data-size-group]'
  );
  const navButtons = el.querySelectorAll<HTMLElement>(
    '.js-ls-product-card__overlay [data-url]'
  );
  const firstColourButton = colourButtons[0] ?? null;

  // One card per colourway: the card is locked to a colour, so that colour's chip drives the
  // overlay instead of the product's first one. Null on a normal card (see product-card.liquid).
  const cardColour = el.dataset.cardColour ?? null;
  const ownColourButton = cardColour
    ? ([...colourButtons].find((btn) => btn.dataset.colour === cardColour) ?? null)
    : null;

  const priceSaleEl       = el.querySelector<HTMLElement>('.js-ls-product-card__price-sale');
  const priceRegularEl    = el.querySelector<HTMLElement>('.js-ls-product-card__price-regular');
  const priceSaleCurrentEl = el.querySelector<HTMLElement>('.js-ls-product-card__price-sale-current');
  const priceSaleCompareEl = el.querySelector<HTMLElement>('.js-ls-product-card__price-sale-compare');

  const initPriceSaleHidden      = priceSaleEl?.classList.contains('hidden') ?? true;
  const initPriceRegularHidden   = priceRegularEl?.classList.contains('hidden') ?? false;
  const initPriceSaleCurrentText = priceSaleCurrentEl?.textContent ?? '';
  const initPriceSaleCompareText = priceSaleCompareEl?.textContent ?? '';
  const initPriceRegularText     = priceRegularEl?.textContent ?? '';

  function swapImage(src: string) {
    if (!img) return;
    if (src) img.src = src;
    else img.removeAttribute('src');
    if (isVideoOverlay) img.classList.toggle('opacity-0', !src);
  }

  // `pressed` controls only the chip's selected highlight. The size group of
  // `active` is always revealed. On card hover we reveal the first colour's
  // sizes without highlighting the chip (pressed: false); hovering a chip
  // itself highlights it (pressed defaults to true).
  function setActiveColour(active: HTMLElement | null, pressed = true) {
    const activeColour = active?.dataset.colour ?? null;
    colourButtons.forEach(btn => {
      btn.setAttribute('aria-pressed', String(pressed && btn === active));
    });
    sizeGroups.forEach(group => {
      group.hidden = group.dataset.sizeGroup !== activeColour;
    });
  }

  function updatePrice(price: string | undefined, comparePrice: string | undefined) {
    if (!priceSaleEl || !priceRegularEl || !priceSaleCurrentEl || !priceSaleCompareEl) return;
    const isSale = !!comparePrice;
    priceSaleEl.classList.toggle('hidden', !isSale);
    priceRegularEl.classList.toggle('hidden', isSale);
    if (isSale) {
      priceSaleCurrentEl.textContent = price ?? '';
      priceSaleCompareEl.textContent = comparePrice ?? '';
    } else {
      priceSaleCurrentEl.textContent = '';
      priceSaleCompareEl.textContent = '';
      priceRegularEl.textContent = price ?? '';
    }
  }

  function resetPrice() {
    if (!priceSaleEl || !priceRegularEl || !priceSaleCurrentEl || !priceSaleCompareEl) return;
    priceSaleEl.classList.toggle('hidden', initPriceSaleHidden);
    priceRegularEl.classList.toggle('hidden', initPriceRegularHidden);
    priceSaleCurrentEl.textContent = initPriceSaleCurrentText;
    priceSaleCompareEl.textContent = initPriceSaleCompareText;
    priceRegularEl.textContent = initPriceRegularText;
  }

  const desktopMql = window.matchMedia('(min-width: 1024px)');

  // Matches the overlay's transition-opacity duration-base (.3s).
  const OVERLAY_FADE_MS = 300;
  let leaveResetTimer: number | null = null;

  const clearLeaveReset = () => {
    if (leaveResetTimer !== null) {
      clearTimeout(leaveResetTimer);
      leaveResetTimer = null;
    }
  };

  function onCardEnter() {
    if (!desktopMql.matches) return;
    clearLeaveReset();
    if (hoverImage) swapImage(hoverImage);
    if (ownColourButton) {
      // The card is already rendered for this colour, price included, so only the sizes have to
      // be revealed. The chip is highlighted because the colour IS the card's selection.
      setActiveColour(ownColourButton);
      return;
    }
    // Reveal the first colour's sizes but leave it unselected (no highlight).
    setActiveColour(firstColourButton, false);
    if (firstColourButton) {
      updatePrice(firstColourButton.dataset.price, firstColourButton.dataset.comparePrice);
    }
  }

  function onCardLeave() {
    if (!desktopMql.matches) return;
    // Restore unconditionally: a colour chip swaps the image even when the card has no hover image.
    swapImage(originalSrc);
    if (firstColourButton) {
      resetPrice();
      // Defer clearing the active colour / size group until the overlay has
      // faded out. Otherwise the size group is display:none'd instantly while
      // the overlay (and colour row) fade via transition-opacity, so the rows
      // disappear staggered instead of together (visible when an OOS click
      // opens the BIS drawer and the card loses hover).
      clearLeaveReset();
      leaveResetTimer = window.setTimeout(() => {
        // A colourway card rests on its own colour, not on "nothing selected".
        setActiveColour(ownColourButton);
        leaveResetTimer = null;
      }, OVERLAY_FADE_MS);
    }
  }

  const enterHandlers = new Map<HTMLElement, () => void>();

  colourButtons.forEach((btn) => {
    const enter = () => {
      if (!desktopMql.matches) return;
      const src = btn.dataset.image;
      if (src) swapImage(src);
      setActiveColour(btn);
      updatePrice(btn.dataset.price, btn.dataset.comparePrice);
    };
    enterHandlers.set(btn, enter);
    btn.addEventListener('mouseenter', enter);
  });

  const priceEnterHandlers = new Map<HTMLElement, () => void>();

  navButtons.forEach((btn) => {
    const enter = () => {
      if (!desktopMql.matches || !btn.dataset.price) return;
      updatePrice(btn.dataset.price, btn.dataset.comparePrice);
    };

    priceEnterHandlers.set(btn, enter);
    btn.addEventListener('mouseenter', enter);
  });

  // Open the BIS drawer for an OOS selector button.
  function openBisDrawer(button: HTMLButtonElement) {
    document.dispatchEvent(
      new CustomEvent('bis:open', {
        detail: {
          product: button.dataset.product,
          variant: button.dataset.variant,
        },
      })
    );
  }

  function navigateToProduct(url: string, event: MouseEvent) {
    if (event.ctrlKey || event.metaKey || event.button === 1) {
      event.preventDefault();
      window.open(url, '_blank', 'noopener');
      return;
    }

    window.location.href = url;
  }

  function onNavClick(e: MouseEvent) {
    // Stop the card-level handler from also navigating: the selector's own
    // destination must win over the card's base URL.
    e.stopPropagation();
    const unit = e.currentTarget as HTMLElement;

    if (unit.dataset.oos === 'true') {
      // OOS units stay <button>: they open the BIS drawer, they don't navigate.
      e.preventDefault();
      openBisDrawer(unit as HTMLButtonElement);
      return;
    }

    // In-stock units are real <a href>, so the browser handles the click
    // itself: plain navigation plus cmd/ctrl/middle/right-click open-in-new-tab.
  }

  function onCardClick(e: MouseEvent) {
    const target = e.target as HTMLElement;
    let url = el.dataset.url;
    if (!url) return;

    if (target.closest('.js-ls-product-card__image-link')) e.preventDefault();

    const activeColour = [...colourButtons].find(
      (btn) => btn.getAttribute('aria-pressed') === 'true'
    );

    // Carry the active (hovered) colour to the PDP so it lands preselected,
    // mirroring a click on the colour selector itself. The card always
    // navigates to the PDP; OOS handling stays on the selectors (onNavClick).
    // A colourway card falls back to its own colour, which is what a click with no hover
    // means below md, where the overlay never opens.
    const colourValue = activeColour?.dataset.value ?? ownColourButton?.dataset.value;

    if (colourValue) {
      const sep = url.includes('?') ? '&' : '?';
      url += `${sep}ls_colour=${encodeURIComponent(colourValue)}`;
    }

    navigateToProduct(url, e);
  }

  navButtons.forEach(btn => btn.addEventListener('click', onNavClick));

  el.addEventListener('click', onCardClick);
  el.addEventListener('mouseenter', onCardEnter);
  el.addEventListener('mouseleave', onCardLeave);

  return {
    unload() {
      hoverPreloader?.unobserve(el);
      clearLeaveReset();
      el.removeEventListener('click', onCardClick);
      el.removeEventListener('mouseenter', onCardEnter);
      el.removeEventListener('mouseleave', onCardLeave);
      enterHandlers.forEach((enter, btn) => {
        btn.removeEventListener('mouseenter', enter);
      });
      priceEnterHandlers.forEach((enter, btn) => {
        btn.removeEventListener('mouseenter', enter);
      });
      navButtons.forEach(btn => btn.removeEventListener('click', onNavClick));
    },
  };
},{
  deferred: false,
});
